import type { ReactNode } from "react";

import { Checkbox } from "@/components/ui/checkbox";
import {
  DIALOG_CLOSE_ACTIONS_CLEARANCE,
  FORM_FIELD_STACK,
  FORM_HINT,
  FORM_LABEL,
  READ_ONLY_BANNER_CLASSES,
  SETTINGS_DIALOG_HEADER,
  SETTINGS_DIALOG_SHELL,
  SETTINGS_FOOTER,
  SETTINGS_SCROLL_INNER,
  SETTINGS_SCROLL_OUTER,
} from "@/lib/surface-styles";
import { cn } from "@/lib/utils";

import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  type DialogSize,
} from "@/components/ui/dialog";

/** Standard settings / hub dialog shell (matches Profile + Workspace settings). */
export function SettingsDialogContent({
  children,
  size = "lg",
  className,
  ...props
}: React.ComponentProps<typeof DialogContent>) {
  return (
    <DialogContent
      size={size}
      className={cn(SETTINGS_DIALOG_SHELL, className)}
      {...props}
    >
      {children}
    </DialogContent>
  );
}

export function SettingsDialogHeader({
  title,
  description,
  actions,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  /** Trailing header action — offset from the dialog close button. */
  actions?: ReactNode;
  className?: string;
}) {
  if (actions) {
    return (
      <div className={cn("flex items-start justify-between gap-4", className)}>
        <DialogHeader
          className={cn(SETTINGS_DIALOG_HEADER, "min-w-0 flex-1 pr-0")}
        >
          <DialogTitle>{title}</DialogTitle>
          {description ? (
            <DialogDescription>{description}</DialogDescription>
          ) : null}
        </DialogHeader>
        <div className={cn(DIALOG_CLOSE_ACTIONS_CLEARANCE, "shrink-0")}>
          {actions}
        </div>
      </div>
    );
  }

  return (
    <DialogHeader className={cn(SETTINGS_DIALOG_HEADER, className)}>
      <DialogTitle>{title}</DialogTitle>
      {description ? (
        <DialogDescription>{description}</DialogDescription>
      ) : null}
    </DialogHeader>
  );
}

export function SettingsDialogScrollBody({
  children,
  className,
  innerClassName,
}: {
  children: ReactNode;
  className?: string;
  innerClassName?: string;
}) {
  return (
    <div className={cn(SETTINGS_SCROLL_OUTER, className)}>
      <div className={cn(SETTINGS_SCROLL_INNER, innerClassName)}>
        {children}
      </div>
    </div>
  );
}

export function SettingsDialogFooter({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <DialogFooter
      className={cn(SETTINGS_FOOTER, "border-stroke-card", className)}
    >
      {children}
    </DialogFooter>
  );
}

export function SettingsFormField({
  id,
  label,
  description,
  children,
  className,
}: {
  id?: string;
  label: string;
  description?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn(FORM_FIELD_STACK, className)}>
      <label htmlFor={id} className={FORM_LABEL}>
        {label}
      </label>
      {description ? <p className={FORM_HINT}>{description}</p> : null}
      {children}
    </div>
  );
}

export function SettingsReadOnlyBanner({ children }: { children: ReactNode }) {
  return <p className={READ_ONLY_BANNER_CLASSES}>{children}</p>;
}

export function SettingsCheckboxField({
  id,
  label,
  description,
  checked,
  onCheckedChange,
  disabled,
  className,
}: {
  id: string;
  label: ReactNode;
  description?: ReactNode;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <div className={cn(FORM_FIELD_STACK, className)}>
      <div className="flex items-start gap-3">
        <Checkbox
          id={id}
          checked={checked}
          onCheckedChange={(value) => onCheckedChange(value === true)}
          disabled={disabled}
          className="mt-0.5"
        />
        <div className="min-w-0 space-y-1">
          <label htmlFor={id} className={cn(FORM_LABEL, "cursor-pointer")}>
            {label}
          </label>
          {description ? <p className={FORM_HINT}>{description}</p> : null}
        </div>
      </div>
    </div>
  );
}

export type { DialogSize };
